import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  StickyNote, Plus, Trash2, CheckCircle2, Clock, AlertTriangle,
  Calendar, MapPin, Phone, Mail, Search, Download,
  X, Edit2, ArrowUpRight, Bell, ChevronRight, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PRIORITY_COLORS = {
  urgent: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  normal: "bg-blue-100 text-blue-700 border-blue-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};
const PRIORITY_DOT = { urgent: "bg-red-500", high: "bg-orange-500", normal: "bg-blue-500", low: "bg-slate-300" };
const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  done: "bg-emerald-100 text-emerald-700",
  canceled: "bg-slate-100 text-slate-500",
};
const CATEGORY_LABELS = { general: "General", sourcing: "Source / Find", follow_up: "Follow Up", meeting: "Meeting Note", backorder: "Backorder" };

const emptyForm = { name: "", location: "", request: "", email: "", phone: "", priority: "normal", follow_up_date: "", category: "general" };

export default function QuickNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const { token } = useAuth();
  const [form, setForm] = useState({ ...emptyForm });

  const fetchNotes = async () => {
    try {
      const url = filter === "all" ? `${API}/admin/quick-notes` : `${API}/admin/quick-notes?status=${filter}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setNotes(data.notes); }
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { fetchNotes(); }, [filter]);

  const openNew = () => { setForm({ ...emptyForm }); setEditingNote(null); setShowForm(true); };

  const openEdit = (note) => {
    setForm({
      name: note.name || "", location: note.location || "", request: note.request || "",
      email: note.email || "", phone: note.phone || "", priority: note.priority || "normal",
      follow_up_date: note.follow_up_date || "", category: note.category || "general",
    });
    setEditingNote(note);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.request) { toast.error("Name and request are required"); return; }
    try {
      if (editingNote) {
        const res = await fetch(`${API}/admin/quick-notes/${editingNote.id}`, {
          method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (res.ok) { toast.success("Note updated!"); setShowForm(false); fetchNotes(); }
      } else {
        const res = await fetch(`${API}/admin/quick-notes`, {
          method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (res.ok) { toast.success("Note saved!"); setShowForm(false); fetchNotes(); }
      }
    } catch { toast.error("Failed to save"); }
  };

  const updateStatus = async (noteId, status) => {
    try {
      await fetch(`${API}/admin/quick-notes/${noteId}`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      fetchNotes();
      toast.success(`Marked as ${status.replace("_", " ")}`);
    } catch { }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    await fetch(`${API}/admin/quick-notes/${noteId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    fetchNotes(); toast.success("Deleted");
  };

  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const res = await fetch(`${API}/admin/quick-notes/send-digest`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) toast.success(data.message || "Daily digest sent to your email!");
      else toast.error(data.detail || "Failed to send");
    } catch { toast.error("Failed to send digest"); }
    finally { setDigestSending(false); }
  };

  const downloadICS = (noteId) => {
    window.open(`${API}/admin/quick-notes/outlook-ics/${noteId}?token=${token}`, "_blank");
  };

  const filtered = notes.filter(n => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return n.name?.toLowerCase().includes(q) || n.request?.toLowerCase().includes(q) || n.location?.toLowerCase().includes(q);
  });

  const openNotes = notes.filter(n => n.status === "open" || n.status === "in_progress");
  const urgentCount = openNotes.filter(n => n.priority === "urgent" || n.priority === "high").length;

  return (
    <div className="space-y-6" data-testid="quick-notes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <StickyNote className="w-6 h-6 text-amber-500" /> Quick Notes
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {openNotes.length} open {urgentCount > 0 && <span className="text-red-600 font-medium">({urgentCount} urgent)</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={sendDigest} disabled={digestSending} data-testid="send-digest-btn">
            <Bell className="w-4 h-4 mr-1" /> {digestSending ? "Sending..." : "Email Digest"}
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-note-btn">
            <Plus className="w-4 h-4 mr-2" /> Quick Note
          </Button>
        </div>
      </div>

      {/* Open Notes Headline List */}
      {openNotes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden" data-testid="open-notes-headline">
          <div className="px-4 py-2.5 bg-amber-100/50 border-b border-amber-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {openNotes.length} Open Item{openNotes.length !== 1 ? "s" : ""} — Click to Review
            </h3>
          </div>
          <div className="divide-y divide-amber-100">
            {openNotes.map((note) => (
              <div
                key={note.id}
                className="group cursor-pointer"
                data-testid={`headline-${note.id}`}
              >
                <div
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-amber-100/40 transition-colors"
                  onClick={() => setExpandedId(expandedId === note.id ? null : note.id)}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[note.priority] || PRIORITY_DOT.normal}`} />
                  <span className="text-sm font-medium text-slate-900 flex-1 truncate">{note.name}</span>
                  <span className="text-xs text-slate-500 hidden sm:block truncate max-w-[200px]">{note.request?.substring(0, 50)}{note.request?.length > 50 ? "..." : ""}</span>
                  {note.location && <span className="text-xs text-slate-400 hidden md:flex items-center gap-1"><MapPin className="w-3 h-3" />{note.location}</span>}
                  {note.follow_up_date && <span className="text-xs text-blue-600 font-medium hidden md:flex items-center gap-1"><Calendar className="w-3 h-3" />{note.follow_up_date}</span>}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${STATUS_COLORS[note.status]}`}>{(note.status || "").replace("_", " ").toUpperCase()}</span>
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expandedId === note.id ? "rotate-90" : ""}`} />
                </div>

                {/* Expanded inline detail */}
                {expandedId === note.id && (
                  <div className="px-4 pb-3 pt-1 bg-white border-t border-amber-100">
                    <p className="text-sm text-slate-700 mb-3">{note.request}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mb-3 flex-wrap">
                      {note.email && <a href={`mailto:${note.email}`} className="flex items-center gap-1 text-blue-600 hover:underline"><Mail className="w-3 h-3" />{note.email}</a>}
                      {note.phone && <a href={`tel:${note.phone}`} className="flex items-center gap-1"><Phone className="w-3 h-3" />{note.phone}</a>}
                      <span className="text-slate-400">Created {new Date(note.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openEdit(note); }}>
                        <Edit2 className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      {note.status === "open" && (
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); updateStatus(note.id, "in_progress"); }}>
                          <Clock className="w-3 h-3 mr-1" /> In Progress
                        </Button>
                      )}
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); updateStatus(note.id, "done"); }}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Close
                      </Button>
                      {note.follow_up_date && (
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); downloadICS(note.id); }}>
                          <Download className="w-3 h-3 mr-1" /> Outlook
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto" onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search notes..." className="pl-9" data-testid="search-notes" />
        </div>
        <div className="flex gap-1">
          {[
            { value: "all", label: "All" },
            { value: "open", label: "Open" },
            { value: "in_progress", label: "In Progress" },
            { value: "done", label: "Done" },
          ].map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Full Notes List */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <StickyNote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-slate-700 font-medium mb-1">{filter === "all" ? "No notes yet" : `No ${filter.replace("_", " ")} notes`}</h3>
          <p className="text-slate-500 text-sm mb-4">Add a quick note to track requests, follow-ups, and meeting items</p>
          <Button onClick={openNew} variant="outline"><Plus className="w-4 h-4 mr-2" /> Add First Note</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((note) => (
            <div key={note.id} className={`bg-white border rounded-xl p-4 hover:shadow-sm transition-all ${note.status === "done" ? "opacity-50 border-slate-200" : "border-slate-200"}`} data-testid={`note-${note.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-1.5 min-h-[48px] rounded-full flex-shrink-0 ${PRIORITY_DOT[note.priority] || PRIORITY_DOT.normal}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-semibold text-slate-900">{note.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${PRIORITY_COLORS[note.priority]}`}>{note.priority?.toUpperCase()}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[note.status]}`}>{(note.status || "open").replace("_", " ").toUpperCase()}</span>
                        <span className="px-2 py-0.5 bg-slate-50 text-slate-500 rounded-full text-xs">{CATEGORY_LABELS[note.category] || note.category}</span>
                      </div>
                      <p className="text-sm text-slate-700 mb-2">{note.request}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                        {note.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{note.location}</span>}
                        {note.email && <a href={`mailto:${note.email}`} className="flex items-center gap-1 text-blue-600 hover:underline"><Mail className="w-3 h-3" />{note.email}</a>}
                        {note.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{note.phone}</span>}
                        {note.follow_up_date && <span className="flex items-center gap-1 text-blue-600 font-medium"><Calendar className="w-3 h-3" />Follow up: {note.follow_up_date}</span>}
                        <span><Clock className="w-3 h-3 inline" /> {new Date(note.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => openEdit(note)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors" title="Edit"><Edit2 className="w-4 h-4" /></button>
                      {note.status !== "done" && (
                        <button onClick={() => updateStatus(note.id, note.status === "open" ? "in_progress" : "done")} className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors" title={note.status === "open" ? "Mark In Progress" : "Mark Done"}>
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      {note.follow_up_date && <button onClick={() => downloadICS(note.id)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors" title="Add to Outlook"><Download className="w-4 h-4" /></button>}
                      {note.email && <a href={`mailto:${note.email}`} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors" title="Email"><ArrowUpRight className="w-4 h-4" /></a>}
                      <button onClick={() => deleteNote(note.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg" data-testid="quick-note-form">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-amber-500" /> {editingNote ? "Edit Note" : "Quick Note"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. Johnson" className="mt-1" required data-testid="qn-name" /></div>
              <div><Label className="text-xs">Location / Hospital</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Memorial Hermann - TMC" className="mt-1" data-testid="qn-location" /></div>
            </div>
            <div><Label className="text-xs">Request / Note *</Label><Textarea value={form.request} onChange={(e) => setForm({ ...form, request: e.target.value })} placeholder="Source 50 cases nitrile gloves... Follow up with OR director..." className="mt-1 min-h-[80px]" required data-testid="qn-request" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="dr.johnson@hospital.org" className="mt-1" data-testid="qn-email" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(713) 555-0123" className="mt-1" data-testid="qn-phone" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Priority</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="sourcing">Source / Find</SelectItem><SelectItem value="follow_up">Follow Up</SelectItem><SelectItem value="meeting">Meeting Note</SelectItem><SelectItem value="backorder">Backorder</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Follow-Up Date</Label><Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className="mt-1" data-testid="qn-followup" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="qn-save">
                <StickyNote className="w-4 h-4 mr-2" /> {editingNote ? "Update Note" : "Save Note"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
